import { IAppThunk } from "../app/store";
import { z } from "zod";
import { selectActiveMcbimSpace, selectHttpMcbimSpacesResult } from "../McBim/mcbimSelectors";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  addSelectedRoomToRedux,
  clearNonPublischedDevicesFromAlgosInRedux,
  removeNonPublischedDevicesFromAlgosInRedux,
  removeSelectedRoomFromRedux,
  resetReviewModeInRedux,
  resetSpaceValidationRunInRedux,
  setActiveColorPanelItemInRedux,
  setActiveLeftSideBarTabInRedux,
  setActiveModuleInRedux,
  setAlgosInProgressInRedux,
  setCopySpaceModeActiveInRedux,
  setCountAlgoSpaceComplete,
  setCountAlgoSpaceFailed,
  setCountAlgoSpaceTotal,
  setDragSelectSpacesInRedux,
  setFreeTextSearchInRedux,
  setMeasurementToolActiveInRedux,
  setSelectedRoomsInRedux,
  setSelectedRoomTabInRedux,
  setSelectedSpaceNumberInRedux,
  thunkToast,
} from "./designSlice";
import { selectDeviceById, selectHttpDevices } from "./devices/deviceSelectors";
import { clickOnDeviceThunk, copyDeviceThunk, rotateDeviceThunk } from "./devices/deviceSlice";
import {
  IMLPredictDevice,
  IDevice,
  IRoomsSummaryHashmap,
  IRoomSummary,
} from "@backend/src/schemas";
import { openapi } from "../../openapi";
import { RIGHT_CLICK_DEVICE_MENU_ENUM, DEVICE_DISCIPLINE_MAPPING, DisciplineEnum } from "../enums";
import {
  selectHttpGetRoomsCurrentFloorResult,
  selectActiveSingleRoomSummary,
} from "../Room/roomSelectors";
import { generateRandomString, PartialMcBimSpace } from "../utils";
import {
  IActiveModule,
  LEFT_SIDEBAR_DEFAULT_TAB_BY_MODULE,
  ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE,
} from "./GlobalDesignBar/designModules";
import { copyDevicesFromASpaceToAnotherSpace } from "../snapToGrid/snapToGrid";

export const thunkClickOnAPixPolygon = (pixPolygonId: string | undefined): IAppThunk => {
  return (dispatch, getState) => {
    if (pixPolygonId == null) {
      return;
    }

    // Check if user click on a device or a space
    const devices = selectHttpDevices(getState());
    const device = devices.find((d) => d._id === pixPolygonId);
    if (device != null) {
      dispatch(clickOnDeviceThunk(device));
    }
    const mcbimSpaces = selectHttpMcbimSpacesResult(getState());
    const space = mcbimSpaces.find((s) => s.name === pixPolygonId);
    if (space != null) {
      // Clicked on a space
      dispatch(thunkClickOnSpace(space.name));
    }
  };
};

export const thunkClickOnSpace = (spaceNr: string | undefined): IAppThunk => {
  return (dispatch, getState) => {
    if (spaceNr == null) {
      return;
    }
    const mcbimSpaces = selectHttpMcbimSpacesResult(getState());
    const rooms = selectHttpGetRoomsCurrentFloorResult(getState());
    const devices = selectHttpDevices(getState());
    const selectedSpaceNumber = getState().design.selectedSpaceNumber;
    const shiftSelectRooms = getState().design.shiftSelectRooms;
    const selectedRooms = getState().design.selectedRooms;
    const copySpaceMode = getState().design.copySpaceModeActive;
    const activeModule = getState().design.activeModule;

    if (copySpaceMode) {
      const spaceToCopyTo = mcbimSpaces.find((space) => space.name === spaceNr);

      if (spaceToCopyTo == null) {
        return;
      }
      const spaceToCopyFrom = mcbimSpaces.find((space) => space.name === selectedSpaceNumber);
      if (spaceToCopyFrom == null) {
        console.log("spaceToCopyFrom is null");
        return;
      }
      const roomToCopyTo = rooms?.[spaceNr];
      dispatch(
        thunkCopyDevicesFromSpaceToOther(spaceToCopyFrom, spaceToCopyTo, devices, roomToCopyTo)
      );
      return;
    }

    if (shiftSelectRooms) {
      if (selectedRooms.includes(spaceNr) === false) {
        dispatch(addSelectedRoomToRedux(spaceNr));
      }
      if (selectedRooms.includes(spaceNr)) {
        dispatch(removeSelectedRoomFromRedux(spaceNr));
      }
    } else {
      if (selectedRooms.length === 1) {
        dispatch(setSelectedRoomsInRedux([spaceNr]));
      }
      if (selectedRooms.length === 1 && selectedRooms[0] === spaceNr) {
        dispatch(setSelectedRoomsInRedux([]));
      }
      dispatch(setSelectedSpaceNumberInRedux(spaceNr));
      if (activeModule) {
        // dispatch(setSelectedRoomTabInRedux(ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE[activeModule]));
      }
    }
  };
};

export const thunkHandleRightMenu =
  (action: RIGHT_CLICK_DEVICE_MENU_ENUM, polygonId: string): IAppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const device = selectDeviceById(state, polygonId);
    if (!device) {
      return;
    }
    const room = selectActiveSingleRoomSummary(state);
    const roomIsLocked = !!room?.multiplacerCeilingDevicesLocked?.bool;

    if (roomIsLocked) {
      // NB-UI: Norska texter i UI enligt din standard
      alert("Rommet er låst");
      return;
    }

    switch (action) {
      case RIGHT_CLICK_DEVICE_MENU_ENUM.DELETE: {
        // Använd RTK Query utan hooks i thunks:
        // (Byt endpointnamn till ditt faktiska)
        await dispatch(
          openapi.endpoints.deleteMultiplacerDeviceDeleteDevice.initiate({ deviceId: polygonId })
        ).unwrap();
        return;
      }
      case RIGHT_CLICK_DEVICE_MENU_ENUM.COPY: {
        await dispatch(copyDeviceThunk(polygonId));
        return;
      }
      case RIGHT_CLICK_DEVICE_MENU_ENUM.ROTATE: {
        await dispatch(rotateDeviceThunk({ device, angle: 90 }));
        return;
      }
      default: {
        // Gör switch:en “exhaustive”
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

export const runPredictGenericSpaceThunk = (): IAppThunk => {
  return async (dispatch, getState) => {
    dispatch(setAlgosInProgressInRedux(true));
    const selectedSpaceNumber = getState().design.selectedSpaceNumber;
    if (selectedSpaceNumber == null) {
      alert("Välj ett rum först");
      return;
    }
    const projectNo = getState().design.projectNo;

    const promisePredictedSpace = dispatch(
      openapi.endpoints.postMlPredictCeilingDevices.initiate({
        body: {
          roomNr: selectedSpaceNumber,
          projectNo: projectNo,
        },
      })
    );
    const mcbimSpace = selectActiveMcbimSpace(getState());
    if (mcbimSpace == null) {
      alert("McBim-space not found");
      return;
    }
    const { data: mlResults } = await promisePredictedSpace;
    if (mlResults != null) {
      const ctx = {
        projectNo: String(projectNo),
        floor: mcbimSpace.buildingStory?.name ?? "unknown",
        roomNumber: mcbimSpace.name,
      };
      dispatch(addNonPublischedDevicesFromAlgosInRedux(convertMlBatchToDevices(mlResults, ctx)));
      // dispatch(setMlGenrericSpaceDevicesInRedux(mlResults));
    }
    dispatch(setAlgosInProgressInRedux(false));
  };
};

/** Minsta kontext som behövs för att fylla kraven i ZDevice */
const ZDeviceContext = z.object({
  projectNo: z.string(),
  floor: z.string(),
  roomNumber: z.string(),
  description: z.string().optional(),
  compCode: z.string().optional(),
});
type IDeviceContext = z.infer<typeof ZDeviceContext>;

export const convertMlResponseToDevice = (ml: IMLPredictDevice, ctx: IDeviceContext): IDevice => {
  const { projectNo, floor, roomNumber } = ctx;
  return {
    _id: generateRandomString(10),
    compCode: ctx.compCode ?? "",
    coordinate: ml.coordinate,
    description: ctx.description ?? "",
    deviceCategory: ml.deviceCategory,
    direction: ml.direction,
    floor,
    discipline: DEVICE_DISCIPLINE_MAPPING[ml.deviceCategory] ?? DisciplineEnum.UNKNOWN,
    projectNo,
    roomNumber,
  };
};

export const convertMlBatchToDevices = (mls: IMLPredictDevice[], ctx: IDeviceContext): IDevice[] =>
  mls.map((ml) => convertMlResponseToDevice(ml, ctx));

const getRoomWithhighestStandardRoomValidationMatch = (
  standardRoomValidationKey: string,
  rooms: IRoomsSummaryHashmap
): string | null => {
  let highestMatchRoomNumber: string | null = null;
  let highestMatchCount = 0;

  for (const [roomNumber, room] of Object.entries(rooms)) {
    const validations = room?.validation?.standardRoom || {};
    const matchCount = validations[standardRoomValidationKey]?.summary?.matchRatePct || 0;

    if (matchCount > highestMatchCount) {
      highestMatchCount = matchCount;
      highestMatchRoomNumber = roomNumber;
    }
  }

  return highestMatchRoomNumber;
};

export const changeActiveModuleThunk =
  (nextModule: IActiveModule): IAppThunk =>
  (dispatch, getState) => {
    const prevModule = getState().design.activeModule;

    const selectedSpaceNumber = getState().design.selectedSpaceNumber;

    const spaces = selectHttpMcbimSpacesResult(getState());

    if (selectedSpaceNumber == null && spaces.length > 0) {
      const randomSpace = spaces[Math.floor(Math.random() * spaces.length)].name;
      dispatch(setSelectedSpaceNumberInRedux(randomSpace));
    }

    // 1) Alltid uppdatera modulvärdet
    dispatch(setActiveModuleInRedux(nextModule));

    // 2) Om vi stänger alla moduler -> stäng även vänsterpanel
    if (!nextModule) {
      dispatch(setActiveLeftSideBarTabInRedux(undefined));
      return;
    }

    // 3) Bara om modulen faktiskt ändrades vill vi sätta default-tab
    if (nextModule !== prevModule) {
      dispatch(setActiveLeftSideBarTabInRedux(LEFT_SIDEBAR_DEFAULT_TAB_BY_MODULE[nextModule]));
      dispatch(setSelectedRoomTabInRedux(ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE[nextModule]));
    }
    dispatch(setActiveColorPanelItemInRedux(undefined));
  };

export const dragSelectEndThunk = (ids: string[]): IAppThunk => {
  return (dispatch, getState) => {
    dispatch(setSelectedRoomsInRedux(ids));
    dispatch(setDragSelectSpacesInRedux(false));
  };
};
export const clearDesignThunk = (): IAppThunk => {
  return (dispatch, getState) => {
    const reviewIsActive = getState().design.review.active;
    const algosInProgress = getState().design.algosInProgress;

    const aSpaceBarIsActive = reviewIsActive || algosInProgress;

    if (!aSpaceBarIsActive) {
      dispatch(setSelectedRoomsInRedux([]));
    }

    dispatch(clearNonPublischedDevicesFromAlgosInRedux());
    dispatch(setCountAlgoSpaceTotal(0));
    dispatch(setCountAlgoSpaceComplete(0));
    dispatch(setCountAlgoSpaceFailed(0));
    dispatch(resetSpaceValidationRunInRedux());
    dispatch(setDragSelectSpacesInRedux(false));
    dispatch(resetReviewModeInRedux());
    dispatch(setActiveColorPanelItemInRedux(undefined));
    dispatch(setFreeTextSearchInRedux(""));
    dispatch(setMeasurementToolActiveInRedux(false));
    dispatch(setCopySpaceModeActiveInRedux(false));
  };
};
export interface IRoomMcBimSpaceCombo {
  roomNumber: string;
  rfpMalRom: string;
  polygon: number[][];
  ceilingCoordinate?: number[];
  ceilingDirection?: number[];
}

export const thunkCopyDevicesFromSpaceToOther = (
  spaceToCopyFrom: PartialMcBimSpace,
  spaceToCopyTo: PartialMcBimSpace,
  devices: IDevice[],
  roomToCopyTo?: IRoomSummary
): IAppThunk => {
  return (dispatch, getState) => {
    if (spaceToCopyTo.name === spaceToCopyFrom.name) {
      return;
    }

    const nonPublischedDevicesFromAlgos = getState().design.nonPublischedDevicesFromAlgos;
    const devicesExistsInSpaceToCopyTo = devices.find(
      (device) => device.roomNumber === spaceToCopyTo.name && device.coordinate != null
    );
    if (devicesExistsInSpaceToCopyTo != null) {
      dispatch(
        thunkToast({
          text: "Det finnes allerede enheter i rommet du prøver å kopiere til",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    const existingDevicesInNonPublischedDevices = nonPublischedDevicesFromAlgos.filter(
      (device) => device.roomNumber === spaceToCopyTo.name
    );

    if (existingDevicesInNonPublischedDevices.length === 0) {
      const devicesTopCopy = devices.filter((device) => device.roomNumber === spaceToCopyFrom.name);
      const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
        devicesToCopy: devicesTopCopy,
        spaceToCopyFrom,
        spaceToCopyTo,
        roomToCopyTo: roomToCopyTo,
      });
      if (copiedDevices.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter kunne kopieres fordi målrommet ${spaceToCopyTo.name} skiller seg for mye fra kildrommet ${spaceToCopyFrom.name}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        return;
      }

      dispatch(addNonPublischedDevicesFromAlgosInRedux(copiedDevices));
    } else {
      dispatch(
        removeNonPublischedDevicesFromAlgosInRedux(
          existingDevicesInNonPublischedDevices.map((device) => device._id)
        )
      );
    }
  };
};
